<template>
  <Polygon
    v-for="buildingFeature in buildingFeatures"
    :options="createPolygonOptions(buildingFeature)"
    @click="updateInfoWindow(buildingFeature)"
  />

  <CustomMarker
    v-for="(buildingFeature, index) in buildingFeatures"
    :key="index"
    :options="getBuildingCentreOptions(buildingFeature)"
  >
    <span v-show="isLabelVisible(buildingFeature.properties.showLabelAtZoom)">
      <p id="buildingLabel"> {{ getBuildingName(buildingFeature) }} </p>
    </span>
  </CustomMarker>

  <InfoWindow :options="infoWindowOptions" v-model="isInfoWindowVisible">
    <p id="infoWindow"> {{ infoWindowContent }} </p>
  </InfoWindow>
</template>

<script setup>

import {CustomMarker, InfoWindow, Polygon} from "vue3-google-map";
import {onMounted, ref} from "vue";
import axios from "axios";

const props = defineProps({
  mapZoomLevel: {
    type: Number
  }
})

// holds data about buildings including their names and outlines
const buildingFeatures = ref([]);

// information for building info window
const infoWindowContent = ref("");
const infoWindowOptions = ref(null);
const isInfoWindowVisible = ref(false);


onMounted(async () => {
  buildingFeatures.value = await fetchBuildingFeatures();
});

// populates buildingFeatures
async function fetchBuildingFeatures() {
  let featuresResponse = await axios.get(
    '/map/features?showBuildings=true&showAccessibleRoutes=false&showToilets=false&showFoodOutlets=false' +
    '&showBikeRacks=false&showAttractions=false&showAtms=false',
    {withCredentials: false}
  );

  return featuresResponse.data.features;
}

function isLabelVisible(showLabelAtZoom) {
  return showLabelAtZoom < props.mapZoomLevel;
}

// updates info window with a buildings information when it is clicked
function updateInfoWindow(buildingFeature) {
  infoWindowOptions.value = getBuildingCentreOptions(buildingFeature);
  infoWindowContent.value = getBuildingName(buildingFeature);
  isInfoWindowVisible.value = true;
}

// gets name for buildings and building groups
function getBuildingName(buildingFeature) {
  if (buildingFeature.properties.featureType === 'Building') {
    return buildingFeature.properties.buildingName;
  } else {
    return buildingFeature.properties.buildingGroupName;
  }
}

// creates a polygon options object with the geometry of a buildingFeature
function createPolygonOptions(buildingFeature) {
  if (buildingFeature === undefined) return

  const path = toLatLng(buildingFeature.geometry.coordinates[0]);

  return {
    paths: path,
    strokeColor: '#9D2f2f',
    strokeOpacity: 0.8,
    strokeWeight: 1,
    fillColor: '#F8F8F8',
    fillOpacity: 1.00,
  }
}

// converts an array of coordinate arrays to an array of objects in the form [{lat: x, lng: y}]
function toLatLng(path) {
  const latLngPath = [];

  for (let i = 0; i < path.length; i++) {
    latLngPath.push({lat: path[i][1], lng: path[i][0]});
  }

  return latLngPath;
}

function getBuildingCentreOptions(buildingFeature) {
  return {
    position: getCentre(buildingFeature.geometry.coordinates[0])
  }
}

function getCentre(coordinates) {
  let latTotal = 0;
  let lngTotal = 0;
  let coordinateCount = 0;

  coordinates.forEach((coordinate) => {
    latTotal += Number(coordinate[1]);
    lngTotal += Number(coordinate[0]);
    coordinateCount += 1;
  })

  return {lat: latTotal / coordinateCount, lng: lngTotal / coordinateCount};
}
</script>

<style scoped>
#buildingLabel {
  font-size: 18px;
}

#infoWindow {
  font-weight: bold;
  font-size: 20px;
}
</style>
